<script setup>
import { onMounted, reactive, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import BiBottomDrawer from '~/bi/components/common/bi-bottom-drawer.vue';
import BiMockDataConfirmPopup from '~/bi/components/query-builder/bi-mock-data-confirm-popup.vue';
import BiTableWidget from '~/bi/components/widgets/table-widget/bi-table-widget.vue';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';
import { useBiStore } from '~/bi/store/bi.store';
import { generateMockData } from '~/bi/utils/bi-mock-data-generator.js';
import useAbortController from '~/common/composables/abort-controller.js';

const emit = defineEmits(['continue']);

const { copy } = useClipboard();
const $t = inject('$t');
const bi_store = useBiStore();
const controllers = useAbortController();

const state = reactive({
  is_copied: false,
  force_update: 0,
});

const editor = ref(null);
async function copyToClipboard() {
  copy(bi_store.sql_query);
  state.is_copied = true;
  setTimeout(() => {
    state.is_copied = false;
  }, 2000);
}

async function initializePackages() {
  const { EditorView, basicSetup } = (await import('codemirror'));
  const { sql, PostgreSQL } = await import('@codemirror/lang-sql');
  editor.value = new EditorView({
    doc: bi_store.sql_query || '',
    extensions: [
      basicSetup,
      EditorView.editable.of(false),
      EditorView.lineWrapping, // 👈 enables wrapping
      sql({
        dialect: PostgreSQL,
        upperCaseKeywords: true,
      }),
    ],
    parent: document.getElementById('sqlArea'),
  });
}

onMounted(() => {
  initializePackages();
});

watch(() => bi_store.sql_query, async () => {
  if (editor.value) {
    editor.value.dispatch({
      changes: {
        from: 0,
        to: editor.value.state.doc.length,
        insert: bi_store.sql_query || '',
      },
    });
  }
});

const is_fetching_data = ref(false);
// async function configureChart() {
//   try {
//     is_fetching_data.value = true;
//     await bi_store.getDataFromStages();
//     emit('continue');
//   }
//   finally {
//     is_fetching_data.value = false;
//   }
// }
let last_promise = null;
async function fetchTableData() {
  try {
    if (bi_store.widget_builder_config.query.stages[0]?.columns?.length) {
      is_fetching_data.value = true;
      controllers.abort('bi_data_preview');
      const signal = controllers.add('bi_data_preview');
      last_promise = bi_store.getWidgetData(bi_store.widget_builder_config.query.stages, 'preview', signal);
      await last_promise;
      last_promise.then((widget_builder_data) => {
        if (bi_store.widget_builder_config.query.stages[0]?.columns?.length) {
          bi_store.widget_builder_data = widget_builder_data.data;
          bi_store.widget_builder_metadata = widget_builder_data.metadata;
        }
      });
    }
    else {
      controllers.abort('bi_data_preview');
      bi_store.widget_builder_data = [];
      bi_store.widget_builder_metadata = null;
      bi_store.sql_query = '';
    }
    state.force_update++;
    is_fetching_data.value = false;
  }
  catch (error) {
    is_fetching_data.value = false;
    logger.error(error);
  }
}

watch(() => (bi_store.widget_builder_config_change_detector), async () => {
  // state.changes_detected = true;
  await fetchTableData();
});

const { open: openMockDataConfirm, close: closeMockDataConfirm } = useModal({
  component: BiMockDataConfirmPopup,
  attrs: {
    onClose() {
      closeMockDataConfirm();
    },
    onConfirm() {
      const stages = bi_store.widget_builder_config.query.stages;
      const { data, metadata } = generateMockData(stages);
      const columns_mapping = bi_store.alias_to_column_mapping();
      const { formatWidgetData } = useBiDataFormatter();
      bi_store.widget_builder_data = formatWidgetData(data, metadata, columns_mapping);
      bi_store.widget_builder_metadata = metadata;
      closeMockDataConfirm();
      emit('continue', { is_mock_data: true });
    },
  },
});

function onConfigureChart() {
  if (bi_store.widget_builder_data.length) {
    emit('continue');
  }
  else {
    openMockDataConfirm();
  }
}
</script>

<template>
  <div class="h-full w-full flex flex-col relative">
    <div class="p-6 flex-1 max-h-[calc(100vh-85px)] overflow-auto">
      <div v-if="bi_store.query_config_error" class="flex items-center justify-center w-full h-full">
        <div class="flex flex-col items-center">
          <span class="inline-block bg-red-100 rounded-full p-3 text-red-500 -mt-4">
            <IconHawkAlertTriangle />
          </span>
          <div class="text-sm font-semibold mt-4">
            {{ $t('Invalid query') }}
          </div>
          <div class="text-sm text-gray-600  text-center">
            {{ bi_store.query_config_error?.message }}
          </div>
          <div class="text-sm text-gray-600 w-[80%] mt-2 text-center">
            {{ bi_store.query_config_error?.details }}
          </div>
        </div>
      </div>
      <BiTableWidget
        v-else
        :key="state.force_update"
        :is-builder="true"
        :config="{}"
        :data="bi_store.widget_builder_data"
        :widget-builder-metadata="bi_store.widget_builder_metadata"
      />
    </div>

    <hr>
    <div class="p-6 w-full flex justify-end items-center">
      <HawkButton
        :loading="is_fetching_data"
        @click="onConfigureChart"
      >
        <IconHawkBarChartTen />
        Configure chart
        <IconHawkArrowRight />
      </HawkButton>
    </div>

    <BiBottomDrawer
      show-text="Show SQL Query"
      hide-text="Hide SQL Query"
    >
      <template #default="{ height }">
        <div class="p-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-1 text-sm font-semibold text-gray-700">
              <IconHawkDatabaseTwo />
              SQL Query
            </div>
            <div class="flex items-center gap-2">
              <button
                class="text-xs font-medium text-gray-700 flex items-center gap-1"
                @click="copyToClipboard"
              >
                <IconHawkCheckCircleGreen v-if="state.is_copied" class="w-4 h-4 text-green-600" />
                <IconHawkCopyOne v-else class="w-4 h-4" />
                {{ state.is_copied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
          </div>
          <div class="overflow-auto scrollbar mt-4" :style="{ height: `${height - 80}px` }">
            <div id="sqlArea" class="w-full h-full sql-area" />
          </div>
        </div>
      </template>
    </BiBottomDrawer>
  </div>
</template>

<style lang="scss" scoped>
:deep(.sql-area) {
  .ͼ5 .cm-activeLine,.ͼ5 .cm-activeLineGutter{
    background-color: transparent !important;
  }
  .ͼ5 .cm-gutters{
    display:none !important;
  }
  .cm-content{
    padding:0 !important;
  }
}
</style>
